import React, { useState } from "react";
import { Input, Button, Select, DatePicker, Spin } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import useIsMobile from "../../hooks/useIsMobile";

const { Option } = Select;

const SolutionMetaFilters = ({
    onSearch,
    filtersList,
    filters: parentFilters,
    onReset,
    filtersData,
    loading,
}) => {
    const isMobile = useIsMobile();

    const initialFilters = Array.isArray(filtersList)
        ? filtersList.reduce((acc, filter) => {
            acc[filter] = "";
            return acc;
        }, {})
        : {};

    const [filters, setFilters] = useState(initialFilters);

    React.useEffect(() => {
        if (parentFilters && Object.keys(parentFilters).length === 0) {
            setFilters({});
        } else if (parentFilters) {
            setFilters(parentFilters);
        }
    }, [parentFilters]);

    const handleFilterChange = (key, value) => {
        setFilters((prevFilters) => ({
            ...prevFilters,
            [key]: value,
        }));
    };

    const handleSearch = () => {
        const formattedFilters = { ...filters };

        if (filters.created_from) {
            formattedFilters.created_from = dayjs(filters.created_from).format("YYYY-MM-DD");
        }
        if (filters.created_to) {
            formattedFilters.created_to = dayjs(filters.created_to).format("YYYY-MM-DD");
        }

        // Clean up empty filters
        const cleanFilters = Object.fromEntries(
            Object.entries(formattedFilters).filter(([_, v]) => v !== "" && v !== undefined && v !== null)
        );

        onSearch(cleanFilters);
    };

    const handleReset = async () => {
        setFilters({});
        await onReset();
    };

    const renderFilter = (filter) => {
        const defaultConfig = {
            component: Input,
            props: {
                placeholder: `Filter by ${filter
                    .split("_")
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(" ")
                    } `,
                style: { width: isMobile ? "80vw" : 200 },
                value: filters[filter],
                onChange: (e) => handleFilterChange(filter, e.target.value),
            },
        };

        const filterConfigs = {
            ai_report: {
                component: Select,
                props: {
                    placeholder: "AI Report",
                    style: { width: isMobile ? "80vw" : 180, textAlign: "left" },
                    value: filters.ai_report || undefined,
                    onChange: (value) => handleFilterChange("ai_report", value),
                    allowClear: true,
                },
                options: [
                    { value: "True", label: "Included" },
                    { value: "False", label: "Not Included" },
                ],
            },
            plag_report: {
                component: Select,
                props: {
                    placeholder: "Plag Report",
                    style: { width: isMobile ? "80vw" : 180, textAlign: "left" },
                    value: filters.plag_report || undefined,
                    onChange: (value) => handleFilterChange("plag_report", value),
                    allowClear: true,
                },
                options: [
                    { value: "True", label: "Included" },
                    { value: "False", label: "Not Included" },
                ],
            },
            expert_rating: {
                component: Input,
                props: {
                    placeholder: "Expert Rating",
                    style: { width: isMobile ? "80vw" : 150 },
                    value: filters.expert_rating || "",
                    onChange: (e) => handleFilterChange("expert_rating", e.target.value),
                },
            },
            task_type: {
                component: Select,
                props: {
                    placeholder: "Task Type",
                    style: { width: isMobile ? "80vw" : 150, textAlign: "left" },
                    value: filters.task_type || undefined,
                    onChange: (value) => handleFilterChange("task_type", value),
                    allowClear: true,
                },
                options: [
                    { value: "regular", label: "Regular" },
                    { value: "feedback", label: "Feedback" },
                    { value: "milestone", label: "Milestone" },
                    { value: "amendment", label: "Amendment" },
                ],
            },
            author: {
                component: Select,
                props: {
                    placeholder: "Author",
                    style: { width: isMobile ? "80vw" : 200, textAlign: "left" },
                    value: filters.author || undefined,
                    onChange: (value) => handleFilterChange("author", value),
                    allowClear: true,
                    showSearch: true,
                    optionFilterProp: "children",
                },
                options: filtersData?.authors || [],
            },
            english_country: {
                component: Select,
                props: {
                    placeholder: "English Country",
                    style: { width: isMobile ? "80vw" : 200, textAlign: "left" },
                    value: filters.english_country || undefined,
                    onChange: (value) => handleFilterChange("english_country", value),
                    allowClear: true,
                    showSearch: true,
                    optionFilterProp: "children",
                },
                options: filtersData?.english_countries || [],
            },
            referencing_format: {
                component: Select,
                props: {
                    placeholder: "Referencing Format",
                    style: { width: isMobile ? "80vw" : 200, textAlign: "left" },
                    value: filters.referencing_format || undefined,
                    onChange: (value) => handleFilterChange("referencing_format", value),
                    allowClear: true,
                    showSearch: true,
                    optionFilterProp: "children",
                },
                options: filtersData?.referencing_formats || [],
            },
            subscriber: {
                component: Input,
                props: {
                    placeholder: "Subscriber",
                    style: { width: isMobile ? "80vw" : 180 },
                    value: filters.subscriber || "",
                    onChange: (e) => handleFilterChange("subscriber", e.target.value),
                },
            },
            key: {
                component: Input,
                props: {
                    placeholder: "Order Key",
                    style: { width: isMobile ? "80vw" : 200 },
                    value: filters.key || "",
                    onChange: (e) => handleFilterChange("key", e.target.value),
                },
            },
            created_from: {
                component: DatePicker,
                props: {
                    style: { width: isMobile ? "80vw" : 200, textAlign: "left" },
                    placeholder: "Created From",
                    value: filters.created_from ? dayjs(filters.created_from) : null,
                    onChange: (value) => handleFilterChange("created_from", value),
                },
            },
            created_to: {
                component: DatePicker,
                props: {
                    style: { width: isMobile ? "80vw" : 200, textAlign: "left" },
                    placeholder: "Created To",
                    value: filters.created_to ? dayjs(filters.created_to) : null,
                    onChange: (value) => handleFilterChange("created_to", value),
                },
            },
        };

        const config = filterConfigs[filter] || defaultConfig;
        const Component = config.component;

        if (config.options) {
            return (
                <Component {...config.props}>
                    {config.options.map(({ value, label }) => (
                        <Option key={value} value={value}>
                            {label}
                        </Option>
                    ))}
                </Component>
            );
        }

        return <Component {...config.props} />;
    };

    return (
        <div style={{ width: "100%", marginBottom: "20px" }}>
            <div
                style={{ background: "#eceff1", padding: "20px", borderRadius: "8px" }}>
                {loading ? (
                    <Spin />
                ) : (
                    <div
                        style={{
                            marginBottom: 16,
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "28px",
                            alignItems: "flex-end",
                            width: "100%",
                        }}>
                        {filtersList?.map((filter) => (
                            <div key={filter}>
                                {renderFilter(filter)}
                            </div>
                        ))}

                        <div
                            style={{
                                display: "flex",
                                gap: "20px",
                                justifyContent: "flex-end",
                                alignContent: "flex-end",
                                alignItems: "flex-end",
                                position: "relative",
                                right: isMobile ? "0" : "10px",
                                flexDirection: isMobile ? "column" : "row",
                                width: "100%",
                            }}>
                            <Button
                                type="primary"
                                style={{
                                    width: isMobile ? "80vw" : 140,
                                    background: "#040724",
                                }}
                                icon={<SearchOutlined />}
                                onClick={handleSearch}>
                                Search
                            </Button>
                            <Button
                                style={{ width: isMobile ? "80vw" : 140 }}
                                type="default"
                                onClick={handleReset}>
                                Reset
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SolutionMetaFilters;
